import React from 'react';
import { Edit2, Copy, Trash2, Calendar, Tag, FileText, Hash, Clock, ArrowRight } from 'lucide-react';
import { formatBRL, formatDateBR } from '../lib/format';
import BotaoFechar, { useEscFecha } from './BotaoFechar';

export default function TransactionDetailsModal({ transaction, pots, accounts, entities, sources, onClose, onEdit, onDuplicate, onDelete }) {
  useEscFecha(onClose);
  if (!transaction) return null;
  const t = transaction;
  const potName = (id) => pots.find((x) => x.id === id)?.name;
  const accName = (id) => accounts.find((x) => x.id === id)?.name;
  const entName = entities.find((x) => x.id === t.entity_id)?.name;
  const srcName = sources.find((x) => x.id === t.revenue_source_id)?.name;

  const typeStyle = t.type === 'income' ? 'bg-emerald-500' : t.type === 'expense' ? 'bg-rose-500' : 'bg-blue-500';
  const typeLabel = t.type === 'income' ? 'Receita' : t.type === 'expense' ? 'Despesa' : 'Transferência';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className={`${typeStyle} text-white p-5 rounded-t-2xl relative`}>
          <div className="absolute top-2.5 right-2.5">
            <BotaoFechar onClose={onClose} tom="claro" testid="tx-detalhes-fechar" />
          </div>
          <div className="text-sm opacity-80 pr-12">{typeLabel}</div>
          <div className="text-3xl font-bold mt-1">{t.type === 'expense' ? '-' : t.type === 'income' ? '+' : ''}{formatBRL(t.amount)}</div>
          <div className="text-sm opacity-90 mt-1">{t.description || '(sem descrição)'}</div>
        </div>

        <div className="p-5 space-y-4">
          <Row icon={Calendar} label="Data da transação" value={formatDateBR(t.transaction_date)} />
          {t.reference_month && <Row icon={Clock} label="Mês referência" value={t.reference_month} />}
          {(potName(t.pot_exit_id) || potName(t.pot_entry_id)) && (
            <Flow label="Potes" from={potName(t.pot_exit_id)} to={potName(t.pot_entry_id)} />
          )}
          {(accName(t.account_exit_id) || accName(t.account_entry_id)) && (
            <Flow label="Contas" from={accName(t.account_exit_id)} to={accName(t.account_entry_id)} />
          )}
          {entName && <Row icon={Hash} label="Entidade" value={entName} />}
          {srcName && <Row icon={Hash} label="Fonte de receita" value={srcName} />}
          {t.tags?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2"><Tag className="w-4 h-4" /> Tags</div>
              <div className="flex gap-2 flex-wrap">
                {t.tags.map((tg) => <span key={tg.id} className="px-2 py-1 rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium">{tg.name}</span>)}
              </div>
            </div>
          )}
          {t.notes && (
            <div>
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2"><FileText className="w-4 h-4" /> Notas</div>
              <div className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 whitespace-pre-wrap">{t.notes}</div>
            </div>
          )}
          <div className="text-xs text-slate-400 dark:text-slate-500 border-t border-slate-200 dark:border-slate-700 pt-3">
            ID #{t.id} · Criada em {formatDateBR(t.created_at)}
          </div>
        </div>

        <div className="flex gap-2 p-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={() => onDelete?.(t)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30">
            <Trash2 className="w-4 h-4" /> Excluir
          </button>
          <div className="flex-1" />
          <button onClick={() => onDuplicate?.(t)} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
            <Copy className="w-4 h-4" /> Duplicar
          </button>
          <button onClick={() => onEdit?.(t)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-500 text-white">
            <Edit2 className="w-4 h-4" /> Editar
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{value}</div>
    </div>
  );
}

function Flow({ label, from, to }) {
  return (
    <div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{label}</div>
      <div className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200">
        {from ? <span className="px-2 py-1 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">{from}</span> : <span className="text-slate-400">—</span>}
        <ArrowRight className="w-4 h-4 text-slate-400" />
        {to ? <span className="px-2 py-1 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">{to}</span> : <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}
